/**
 * @file app-mesh.ts
 *
 * Mesh I/O and transfer handlers for as-app: download, configurator mesh
 * retrieval, transfer-to-app flow, mesh history, and autoload dialog.
 * All exported functions take a MeshContext as their first argument.
 */

import {
  storeMesh,
  type StoredMeshData,
  checkForStoredMeshData,
  type TransferSaveData,
  setNewApp,
  deleteAllStoredTransfers,
  deleteMesh,
  getSenderApp,
  deleteStaleTransferMeshes,
} from '../../logic/db.ts';
import { fullAppSuiteRootURL, isProduction } from '../../data/constants.ts';
import { devlog, navigateViaAnchor } from '../../logic/utils.ts';
import type { ModalContent } from './modal-templates.ts';
import type { AsModalOverlay } from '../overlays/as-modal-overlay.ts';
import type { UserData } from '../../logic/asApi.ts';
import { appInfo, appInfoCached, getAppInfo } from './app-config.ts';

// ============================================================================
// Context interface — satisfied by AsApp directly
// ============================================================================

/** Detail handed to onTransferMeshLoaded (same shape as as-db-mesh-loaded's). */
export interface TransferMeshDetail {
  /** The transferred mesh as a plain File, named "<meshName>.stl". */
  stl: File;
  /** Id of the stored IDB entry. */
  meshId: string;
  /** The full stored record. */
  mesh: StoredMeshData;
}

/**
 * Rx hook: process a received transfer mesh. Resolve -> as-mesh-rx-completed
 * is dispatched automatically (return { keepStored: true } to keep the stored
 * entry, i.e. shouldDelete: false). Reject -> a persistent error notification
 * offers "Skip transfer" / "Save transfer as file" instead.
 */
export type TransferMeshLoadedCallback =
  (detail: TransferMeshDetail) => Promise<{ keepStored?: boolean } | void> | { keepStored?: boolean } | void;

export interface MeshContext {
  onTransferMeshLoaded?: TransferMeshLoadedCallback | null;
  readonly appId: string;
  meshData: {
    latestMesh: StoredMeshData | null;
    autoLoadMesh: StoredMeshData | null;
    totalCount: number;
    meshes?: StoredMeshData[];
  };
  modalContent: ModalContent;
  availableApps: string[];
  openTransferInNewTab: boolean;
  userData: UserData | null;
  readonly otherAppsForTransfer: string[];
  addToPostInitQueue(op: () => void): void;
  dispatchEvent(ev: Event): boolean;
  readonly modalOverlay: AsModalOverlay | null;
  logError(origin: string, err: unknown, importance?: 'critical' | 'regular' | 'warning', label?: string): void;
  showErrorModal(detail: { error: Error | string; origin?: string; info?: string }): void;
  showModal(): void;
  sendLog(evName: string, subcategoryOrData?: string | object, data?: object): void;
  initializeAvailableApps(): Promise<void>;
  hasAttribute(name: string): boolean;
}

// ============================================================================
// Core mesh retrieval
// ============================================================================

export const getMeshFromConfigurator = (ctx: MeshContext, finalized = false, type = 'stl', filename = 'model.stl'): Promise<Blob> => {
  devlog('getMeshFromConfigurator: Starting mesh request', { finalized, type, filename });
  return new Promise<Blob>((resolve, reject) => {
    ctx.addToPostInitQueue(() => {
      window.dispatchEvent(new CustomEvent('as-get-configurator-mesh', {
        detail: {
          finalized, type, filename,
          resolve: (blob: Blob) => {
            devlog('getMeshFromConfigurator: Received mesh blob', blob);
            if (!blob) {
              const error = Object.assign(new Error('Invalid mesh blob'), { name: 'MeshRequestError' });
              reject(error);
              return;
            }
            resolve(blob);
          },
          reject: (err: Error) => {
            console.error('getMeshFromConfigurator: Error getting mesh:', err);
            reject(Object.assign(err || new Error('Failed to get mesh'), { name: err?.name || 'MeshFetchError' }));
          },
        },
      }));
    });
  });
};

const meshFromConfigurator = async (ctx: MeshContext, finalized = false, type = 'stl', filename = 'model.stl'): Promise<Blob> => {
  devlog('meshFromConfigurator: Starting mesh request with modal', { finalized, type, filename });
  ctx.modalContent = { type: 'saving', data: { status: 'Preparing mesh for download...' } };
  ctx.showModal();
  return getMeshFromConfigurator(ctx, finalized, type, filename);
};

// ============================================================================
// Download
// ============================================================================

export const downloadMesh = async (ctx: MeshContext, finalized = false, type = 'stl', fileHandle?: any): Promise<void> => {
  try {
    let blob: Blob;
    if (fileHandle) {
      blob = await getMeshFromConfigurator(ctx, finalized, type, fileHandle.name);
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
      devlog('Mesh saved successfully to:', fileHandle.name);
    } else {
      blob = await getMeshFromConfigurator(ctx, finalized, type, `mesh.${type}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mesh.${type}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
    ctx.dispatchEvent(new CustomEvent('as-data-export', { bubbles: true, composed: true, detail: { subcategory: 'local', fileInfo: [{ sizeBytes: blob.size, fileType: type }] } }));
  } catch (error: any) {
    if (error?.name === 'AbortError') return;
    ctx.logError('mesh-download', error, 'critical', 'Error downloading mesh:');
    ctx.dispatchEvent(new CustomEvent('as-data-export', {
      bubbles: true, composed: true,
      detail: { subcategory: 'local', error: error?.message ?? String(error), fileInfo: [{ fileType: type }] },
    }));
    ctx.dispatchEvent(new CustomEvent('as-notify', {
      detail: { defaultText: 'Error downloading mesh: ' + error.message, dictKey: 'notifications.mesh.download.error', type: 'error' },
    }));
    ctx.showErrorModal({ error, info: 'Failed to download mesh', origin: 'mesh-download' });
  }
};

// ============================================================================
// Autoload
// ============================================================================

export const showAutoLoadDialog = (ctx: MeshContext, autoLoadMesh: StoredMeshData): void => {
  devlog('Checking for auto-load dialog requirements for mesh:', autoLoadMesh.meshName);
  ctx.modalContent = { type: 'autoload', data: { autoLoadMesh } };
  if (ctx.hasAttribute('confirm-autoload')) {
    devlog('confirm-autoload attribute is present, showing dialog');
    ctx.showModal();
  } else {
    devlog('confirm-autoload attribute is not present, automatically loading mesh');
    handleAutoLoadConfirm(ctx);
  }
};

export const handleAutoLoadConfirm = (ctx: MeshContext): void => {
  const mesh = ctx.modalContent.data?.autoLoadMesh;
  if (!mesh) { console.error('No auto-load mesh found in modal data'); ctx.modalOverlay?.close(); return; }
  ctx.modalOverlay?.close();

  if (mesh.stlData) {
    devlog('Dispatching as-db-mesh-loaded event with STL data, size:', mesh.stlData.size);
    ctx.dispatchEvent(new CustomEvent('as-notify', {
      detail: { defaultText: `Detected mesh: ${mesh.meshName}`, dictKey: 'notifications.mesh.autoload.detected', type: 'info' },
    }));
    const meshWithDelete = { ...mesh, queueDelete: true };
    const stlFile = new File([mesh.stlData], `${mesh.meshName}.stl`, { type: 'application/octet-stream' });
    ctx.addToPostInitQueue(() => {
      // With an onTransferMeshLoaded callback set, the callback owns the Rx
      // flow and the event is suppressed entirely — dispatching both would
      // invite double handling (two uploads of the same mesh).
      if (ctx.onTransferMeshLoaded) {
        runTransferMeshCallback(ctx, { mesh: meshWithDelete, meshId: mesh.meshId, stl: stlFile });
        return;
      }
      ctx.dispatchEvent(new CustomEvent('as-db-mesh-loaded', {
        detail: { mesh: meshWithDelete, meshId: mesh.meshId, stl: stlFile },
        bubbles: true,
        composed: true,
      }));
    });
  } else {
    console.warn('Mesh has no STL data to load');
    setNewApp(mesh.meshId, ctx.appId, false)
      .then(() => devlog('Reset autoLoad flag for mesh without STL data'))
      .catch(err => console.error('Error resetting autoLoad flag:', err));
  }
};

/** Await the app's onTransferMeshLoaded and complete (or fail) the Rx flow for it. */
const runTransferMeshCallback = async (ctx: MeshContext, detail: TransferMeshDetail): Promise<void> => {
  try {
    const result = await ctx.onTransferMeshLoaded!(detail) as { keepStored?: boolean } | undefined;
    ctx.dispatchEvent(new CustomEvent('as-mesh-rx-completed', {
      detail: { meshId: detail.meshId, shouldDelete: !result?.keepStored },
    }));
  } catch (err) {
    ctx.logError('mesh-rx-callback', err, 'regular', 'onTransferMeshLoaded rejected:');
    // Log the failed interapp import right away — the user may resolve the
    // notification much later, or never (X-dismiss retries on next boot).
    ctx.sendLog('data_import_error', 'interapp', {
      error: err instanceof Error ? err.message : String(err),
      fromApp: detail.mesh.lastApp,
      meshId: detail.meshId,
      meshName: detail.mesh.meshName,
      fileInfo: [{ sizeBytes: detail.stl.size, fileType: 'stl' }],
    });
    showTransferRxErrorNotification(ctx, detail, err);
  }
};

/**
 * Persistent error notification for a rejected onTransferMeshLoaded.
 * Both actions close the card themselves (via the id handed back through the
 * as-notify resolve callback) once they have completed the Rx flow:
 *   Skip transfer          keeps the stored mesh but clears its auto-load flag
 *                          (shouldDelete: false) — no retry loop on next boot,
 *                          still recoverable from the history modal.
 *   Save transfer as file  downloads the STL, then deletes the stored entry.
 * Dismissing via the X decides nothing: the entry stays flagged for auto-load
 * and the transfer is retried on the next boot.
 */
const showTransferRxErrorNotification = (ctx: MeshContext, detail: TransferMeshDetail, err: unknown): void => {
  let notifId = '';
  const message = err instanceof Error ? err.message : String(err);
  const finish = (shouldDelete: boolean) => {
    // error in the detail marks this as a failure resolution: the cleanup path
    // still runs, but skips the success data_import log (data_import_error was
    // already sent when the callback rejected).
    ctx.dispatchEvent(new CustomEvent('as-mesh-rx-completed', {
      detail: { meshId: detail.meshId, shouldDelete, error: message },
    }));
    if (notifId) ctx.dispatchEvent(new CustomEvent('as-close-notification', { detail: { id: notifId } }));
  };
  ctx.dispatchEvent(new CustomEvent('as-notify', {
    detail: {
      defaultText: `Could not load the transferred mesh: ${message}`,
      type: 'error',
      persist: true,
      resolve: (id: string) => { notifId = id; },
      actions: [
        {
          defaultText: 'Skip transfer',
          callback: () => finish(false),
        },
        {
          defaultText: 'Save transfer as file',
          callback: () => {
            const url = URL.createObjectURL(detail.stl);
            const a = document.createElement('a');
            a.href = url;
            a.download = detail.stl.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            finish(true);
          },
        },
      ],
    },
  }));
};

export const handleAutoLoadCancel = (ctx: MeshContext): void => {
  const mesh = ctx.modalContent.data?.autoLoadMesh;
  if (mesh) {
    setNewApp(mesh.meshId, ctx.appId, false)
      .then(() => devlog('Reset autoLoad flag after user canceled auto-load'))
      .catch(err => console.error('Error resetting autoLoad flag:', err));
  }
  ctx.modalOverlay?.close();
};

// ============================================================================
// Stale mesh cleanup
// ============================================================================

export const cleanUpStaleMeshes = async (ctx: MeshContext): Promise<void> => {
  try {
    const deleted = await deleteStaleTransferMeshes();
    if (deleted > 0) {
      devlog(`cleanUpStaleMeshes: Deleted ${deleted} stale mesh(es) older than 24 h`);
      ctx.sendLog('stale_meshes_deleted', { count: deleted });
    }
  } catch (err) {
    ctx.logError('mesh-stale-cleanup', err, 'warning', 'Error cleaning up stale meshes:');
  }
};

// ============================================================================
// Mesh history
// ============================================================================

export const handleShowStoredMeshes = async (ctx: MeshContext): Promise<void> => {
  devlog('Received show-stored-meshes event, loading mesh history');
  const meshData = await checkForStoredMeshData(ctx.appId, false);
  if (meshData.totalCount > 0) {
    devlog(`Found ${meshData.totalCount} mesh(es) for app ${ctx.appId}`);
    ctx.modalContent = { type: 'history', data: { meshes: meshData.meshes } };
    ctx.showModal();
  } else {
    devlog('No mesh history found for this app');
  }
};

export const postMeshReceiveCleanup = async (ctx: MeshContext, event: Event): Promise<void> => {
  const { meshId, shouldDelete, error } = (event as CustomEvent).detail;
  if (!meshId) { console.error('No meshId provided in mesh-processed event'); return; }

  const senderApp = await getSenderApp(meshId);
  if (senderApp === null) { console.error('No sender app found for mesh: ', meshId); return; }

  // A failure resolution (error set) was already logged as data_import_error
  // when the Rx callback rejected — don't also log a successful import.
  if (!error) ctx.sendLog('data_import', 'interapp', { fromApp: senderApp });
  try {
    if (shouldDelete !== false) {
      devlog(`Deleting mesh ${meshId} as requested`);
      await deleteMesh(meshId);
    } else {
      devlog(`Setting autoLoad to false for mesh ${meshId}`);
      await setNewApp(meshId, ctx.appId, false);
    }
  } catch (err) {
    ctx.logError('mesh-processing', err, 'critical', 'Error processing mesh:');
    ctx.showErrorModal({ error: err as Error, info: 'Failed to process mesh', origin: 'mesh-processing' });
  }
};

// ============================================================================
// Transfer
// ============================================================================

export const prepareTransferMesh = async (ctx: MeshContext): Promise<void> => {
  devlog('Starting transfer mesh process');
  ctx.openTransferInNewTab = false;

  const configInstance = document.querySelector('as-configurator') as any;
  if (!configInstance) throw new Error('Configurator component not found');
  if (!configInstance.initialized) {
    ctx.dispatchEvent(new CustomEvent('as-notify', {
      detail: { defaultText: 'Cannot transfer mesh: The configurator is not yet initialized. Please wait a moment and try again.', dictKey: 'notifications.mesh.transfer.save.error', type: 'error' },
    }));
    return;
  }

  ctx.modalContent = { type: 'saving', data: { status: 'Preparing mesh for transfer...' } };
  ctx.showModal();

  if (ctx.availableApps.length === 0) await ctx.initializeAvailableApps();

  let availableApps = ctx.otherAppsForTransfer;
  if (availableApps.length === 0) {
    console.warn('[AS-APP] No apps available for transfer on first attempt. Retrying appInfo load. appInfoCached:', appInfoCached);
    try {
      await getAppInfo();
      await ctx.initializeAvailableApps();
      availableApps = ctx.otherAppsForTransfer;
    } catch (error) {
      console.error('[AS-APP] Failed to load appInfo on retry:', error);
    }
    if (availableApps.length === 0) {
      console.error('[AS-APP] No apps available for transfer after retry. appInfoCached:', appInfoCached);
      ctx.dispatchEvent(new CustomEvent('as-notify', {
        detail: { defaultText: 'Cannot transfer mesh: No apps available for transfer. App configuration may not be loaded yet.', dictKey: 'notifications.mesh.transfer.no-apps.error', type: 'error' },
      }));
      ctx.modalOverlay?.close();
      return;
    }
  }

  let pendingStoreData: TransferSaveData;
  if (ctx.meshData.latestMesh) {
    pendingStoreData = {
      authorId: ctx.meshData.latestMesh.authorId,
      meshName: ctx.meshData.latestMesh.meshName,
      meshId: ctx.meshData.latestMesh.meshId,
      currentApp: ctx.appId,
      destinationApp: 'none',
      stlData: null,
    };
  } else {
    const meshId = `mesh-${ctx.appId}-${Date.now()}`;
    pendingStoreData = {
      authorId: ctx.userData?.eMail || 'unknown',
      meshName: `Mesh from ${ctx.appId}`,
      meshId,
      currentApp: ctx.appId,
      destinationApp: 'none',
      stlData: null,
    };
    devlog('No existing mesh data found, created new mesh with ID:', meshId);
  }

  devlog('Getting mesh from configurator...');
  meshFromConfigurator(ctx, true, 'stl', `${pendingStoreData.meshName}.stl`)
    .then(blob => {
      devlog('Got mesh blob, size:', blob.size);
      pendingStoreData.stlData = blob;
      ctx.modalContent = { type: 'success', data: { blob, availableApps, pendingStoreData } };
      ctx.showModal();
    })
    .catch(err => {
      ctx.logError('mesh-transfer', err, 'critical', 'Error in mesh transfer process:');
      ctx.sendLog('data_export_error', 'interapp', { error: err?.message ?? String(err) });
      ctx.showErrorModal({ error: err, info: 'Error occurred during mesh transfer operation (meshFromConfigurator)', origin: 'mesh-transfer' });
      ctx.dispatchEvent(new CustomEvent('as-notify', {
        detail: { defaultText: 'Error during mesh transfer: ' + err.message, dictKey: 'notifications.mesh.transfer.save.error', type: 'error' },
      }));
      ctx.modalOverlay?.close();
    });
};

export const autoTransferToApp = async (ctx: MeshContext, destinationAppId: string): Promise<void> => {
  devlog(`Starting automated transfer mesh to: ${destinationAppId}`);
  ctx.openTransferInNewTab = false;

  const configInstance = document.querySelector('as-configurator') as any;
  if (!configInstance || !configInstance.initialized) {
    console.error('Configurator not ready for auto-transfer');
    return;
  }

  // Set modal to saving state to show progress
  ctx.modalContent = { type: 'saving', data: { status: `Transferring to ${destinationAppId}...` } };
  ctx.showModal();

  let pendingStoreData: TransferSaveData;
  if (ctx.meshData.latestMesh) {
    pendingStoreData = {
      authorId: ctx.meshData.latestMesh.authorId,
      meshName: ctx.meshData.latestMesh.meshName,
      meshId: ctx.meshData.latestMesh.meshId,
      currentApp: ctx.appId,
      destinationApp: destinationAppId,
      stlData: null,
    };
  } else {
    const meshId = `mesh-${ctx.appId}-${Date.now()}`;
    pendingStoreData = {
      authorId: ctx.userData?.eMail || 'unknown',
      meshName: `Mesh from ${ctx.appId}`,
      meshId,
      currentApp: ctx.appId,
      destinationApp: destinationAppId,
      stlData: null,
    };
  }

  try {
    const blob = await getMeshFromConfigurator(ctx, true, 'stl', `${pendingStoreData.meshName}.stl`);
    pendingStoreData.stlData = blob;

    const savedMeshData = await storeMesh(pendingStoreData, true);
    devlog('Auto-transfer mesh saved:', savedMeshData.meshId);
    
    if (!ctx.meshData) ctx.meshData = { latestMesh: null, autoLoadMesh: null, totalCount: 0 };
    ctx.meshData.latestMesh = savedMeshData;
    ctx.modalOverlay?.close();

    await ctx.sendLog('data_export', 'interapp', { toApp: destinationAppId, auto: true });
    
    const currentUrl = new URL(window.location.href);
    const newAppRedirURL = new URL(`${fullAppSuiteRootURL}/apps/${destinationAppId}`);

    // Propagate all current search params
    currentUrl.searchParams.forEach((val, key) => newAppRedirURL.searchParams.set(key, val));

    // Ensure 'mode' is preserved if it was in the fragment but logic detected it
    if (window.location.href.includes('mode=autoShadowBoard') && !newAppRedirURL.searchParams.has('mode')) {
      newAppRedirURL.searchParams.set('mode', 'autoShadowBoard');
    }

    const newTab = currentUrl.searchParams.get('newTab') === 'true';
    if (!isProduction) devlog('auto redir to: ', newAppRedirURL.toString());
    // Anchor click rather than window.open / location.href so the transfer
    // redirect also works in the GrabCAD embedded webview (see navigateViaAnchor).
    navigateViaAnchor(newAppRedirURL.toString(), newTab);

  } catch (err) {
    ctx.logError('auto-mesh-transfer', err, 'critical', 'Error in auto mesh transfer:');
    ctx.sendLog('data_export_error', 'interapp', { toApp: destinationAppId, auto: true, error: (err as Error)?.message ?? String(err) });
    ctx.showErrorModal({ error: err as Error, info: 'Failed to complete automated transfer', origin: 'auto-mesh-transfer' });
  }
};

// ============================================================================
// Transfer modal click handler (delegated from as-app-overlay)
// ============================================================================

export const handleTxModalClick = async (ctx: MeshContext, event: MouseEvent): Promise<void> => {
  const target = event.target as HTMLElement;
  if (!target.closest('[id^="modal-panel-"]')) return;

  const newTabInput = target.closest('#transfer-input-open-in-new-tab') as HTMLInputElement | null;
  if (newTabInput) {
    ctx.openTransferInNewTab = newTabInput.checked;
    devlog('Transfer input open in new tab', ctx.openTransferInNewTab);
  }

  const appButton = target.closest('.app-selection-button');
  if (appButton) {
    event.preventDefault();
    const selectedAppId = appButton.getAttribute('data-app');
    devlog('Selected app:', selectedAppId);

    if (selectedAppId && ctx.modalContent.type === 'success') {
      const pendingData = ctx.modalContent.data?.pendingStoreData;

      if (pendingData) {
        pendingData.destinationApp = selectedAppId;
        ctx.modalContent = { type: 'saving', data: { status: 'Saving mesh to database...' } };
        ctx.showModal();

        storeMesh(pendingData, true)
          .then(async (savedMeshData) => {
            devlog('Mesh saved successfully:', savedMeshData.meshId, 'autoLoad:', savedMeshData.autoLoad);
            if (!ctx.meshData) ctx.meshData = { latestMesh: null, autoLoadMesh: null, totalCount: 0 };
            ctx.meshData.latestMesh = savedMeshData;
            ctx.modalOverlay?.close();

            const someAppInfo = appInfo[selectedAppId];
            devlog(someAppInfo, appInfo);
            try { await ctx.sendLog('data_export', 'interapp', { toApp: selectedAppId }); } catch (err) { console.error('sendLog failed (data_export interapp):', err); }
            const newAppRedirURL = `${fullAppSuiteRootURL}/apps/${selectedAppId}${window.location.search}`;

            if (!isProduction) devlog('redir to: ', newAppRedirURL);

            if (someAppInfo?.id) {
              if (ctx.openTransferInNewTab) devlog('Opening in new tab');
              // Anchor click so the redirect also works in the GrabCAD embedded
              // webview (see navigateViaAnchor).
              navigateViaAnchor(newAppRedirURL, ctx.openTransferInNewTab);
            } else {
              console.warn(`No app link defined for ${selectedAppId}`);
            }
          })
          .catch(err => {
            ctx.logError('mesh-storage', err, 'critical', 'Error saving mesh to database:');
            ctx.sendLog('data_export_error', 'interapp', { toApp: selectedAppId, error: err?.message ?? String(err) });
            ctx.showErrorModal({ error: err, info: 'Failed to save mesh to database', origin: 'mesh-storage' });
          });
      } else if (ctx.meshData.latestMesh) {
        setNewApp(ctx.meshData.latestMesh.meshId, selectedAppId, true)
          .then(() => {
            devlog('Updated mesh destination app to:', selectedAppId, 'with autoLoad=true');
            const someAppInfo = appInfo[selectedAppId];
            if (someAppInfo?.id) {
              const newAppRedirURL = `${fullAppSuiteRootURL}/apps/${selectedAppId}${window.location.search}`;

              navigateViaAnchor(newAppRedirURL, ctx.openTransferInNewTab);
            } else {
              console.warn(`No app link defined for ${selectedAppId}`);
            }
          })
          .catch(err => {
            ctx.logError('mesh-transfer-destination', err, 'critical', 'Error updating mesh destination:');
            ctx.sendLog('data_export_error', 'interapp', { toApp: selectedAppId, error: err?.message ?? String(err) });
            ctx.showErrorModal({ error: err, info: 'Failed to update mesh destination', origin: 'mesh-transfer' });
          });
      }
    }
    return;
  }

  if (ctx.modalContent.type === 'autoload') {
    if (target.closest('#confirm-autoload')) { devlog('User confirmed auto-load'); handleAutoLoadConfirm(ctx); return; }
    if (target.closest('#cancel-autoload')) { devlog('User canceled auto-load'); handleAutoLoadCancel(ctx); return; }
  }

  const clearButton = target.closest('#clear-mesh-history');
  if (clearButton && ctx.modalContent.type === 'history') {
    devlog('Clear mesh history button clicked');
    deleteAllStoredTransfers()
      .then(() => {
        devlog('All mesh history cleared');
        ctx.modalOverlay?.close();
        ctx.meshData = { latestMesh: null, autoLoadMesh: null, totalCount: 0 };
        devlog('Cleared mesh history, refreshing page to ensure clean state');
        window.location.reload();
      })
      .catch(err => {
        ctx.logError('mesh-history-clear', err, 'critical', 'Error clearing mesh history:');
        ctx.showErrorModal({ error: err, info: 'Failed to clear mesh history', origin: 'mesh-history' });
      });
  }
};
